import { useMemo } from 'react'
import type { Trip } from '../api/types'

export type TripPhase = 'planning' | 'upcoming' | 'live' | 'completed'

/**
 * Derive the trip phase that drives the UI mode (Planning vs Live).
 * Explicit `status` wins; otherwise we infer from dates relative to today:
 *  - no dates              -> planning
 *  - today within range    -> live
 *  - start in the future   -> upcoming
 *  - end in the past       -> completed
 */
export function deriveTripPhase(trip: Pick<Trip, 'status' | 'start_date' | 'end_date' | 'time_zone_id'>, now = new Date()): TripPhase {
  if (trip.status === 'live' || trip.status === 'completed') return trip.status

  if (!trip.start_date && !trip.end_date) return 'planning'
  let today: string
  try {
    const parts = new Intl.DateTimeFormat('en-US', {timeZone:trip.time_zone_id ?? 'Etc/UTC', year:'numeric', month:'2-digit', day:'2-digit'}).formatToParts(now)
    const part = (type: string) => parts.find(p=>p.type===type)?.value ?? ''
    today = `${part('year')}-${part('month')}-${part('day')}`
  } catch { today = now.toISOString().slice(0,10) }
  if (trip.start_date && today < trip.start_date) return 'upcoming'
  if (trip.end_date && today > trip.end_date) return 'completed'
  if (trip.start_date && today >= trip.start_date && (!trip.end_date || today <= trip.end_date)) return 'live'
  return trip.status === 'upcoming' ? 'upcoming' : 'planning'
}

export function useTripPhase(
  trip: Pick<Trip, 'status' | 'start_date' | 'end_date' | 'time_zone_id'> | null | undefined,
): TripPhase {
  return useMemo(() => {
    if (!trip) return 'planning'
    return deriveTripPhase(trip)
  }, [trip])
}
